import type { TaskCorrectionRequest } from '../../../agents/planner/task-correction-binding.js';
import type { CompoundSelectionRequest } from '../../../agents/planner/compound-selection.contract.js';
import type { ChatTurnAction } from '../../../agents/context/ui-command-registry.js';
import { parseSseStream } from '@govard/llm-gate-client';
import type { OpenRouterChatModelId } from '@govard/llm-gate-client';

import {
  ApiError,
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  buildHeaders,
} from '../../../shared/api-client/index.js';

import { i18n } from '../../../i18n/index.js';

import type {
  AiChatEventEnvelope,
  ChatContextRef,
  MeetingSnapshotItem,
  StructuredMessagePayload,
  ToolCall,
  Usage,
} from '@govard/ai-chat-kit';

export type { ChatContextRef };

export type ChatContextEntityType = 'deal' | 'contact' | 'project' | 'workspace' | 'none';
export type LlmGatewayProfile = 'neuraldeep' | 'openrouter' | 'govard' | 'opencode';
export type ChatMessageRole = 'user' | 'assistant' | 'tool' | 'system';

export interface ChatMessageItem {
  id: string;
  role: ChatMessageRole;
  content: string;
  structuredPayload?: StructuredMessagePayload | null;
  toolCalls?: ToolCall[] | null;
  toolCallId?: string | null;
  createdAt: string;
}

export interface ChatConversationItem {
  id: string;
  workspaceId: string;
  userId: string;
  title?: string | null;
  contextEntityType: ChatContextEntityType;
  contextEntityId: string;
  contextEntityLabel?: string | null;
  gatewayProfile?: LlmGatewayProfile;
  visibility?: 'private' | 'shared';
  autoApprove?: boolean;
  archivedAt?: string | null;
  sharedAt?: string | null;
  sharedByUserId?: string | null;
  lastMessageAt?: string;
  createdAt?: string;
}

export type PostChatMessageInput = ChatTurnAction & PostChatMessageContext;
export interface PostChatMessageContext {
  clientTimeZone?: string;
  compoundSelection?: CompoundSelectionRequest;
  taskCorrection?: TaskCorrectionRequest;
  batchCorrection?: { sourceMessageId: string } | undefined;
  /** Explicit UI locale for server-owned messages of this turn. */
  locale?: 'ru' | 'en';
  contextEntityType: 'deal' | 'contact' | 'project' | 'none';
  contextEntityId: string;
  contextRefs?: ChatContextRef[];
  /** Read-only ephemeral context for the My day meetings scenario; never persisted. */
  meetingSnapshot?: MeetingSnapshotItem[];
  /**
   * PR 5: conversation-owned attachment sent with this message. The server
   * verifies it belongs to this conversation before any persistence and
   * records provenance on the user message.
   */
  attachmentId?: string;
  /** Stable identity for one durable streamed turn. */
  runId: string;
  turnId: string;
  /** `false` — ход без размышления модели; ставится переключателем в шапке чата. */
  reasoning?: boolean;
  /** Local OpenRouter preference for this turn; never persisted in a conversation. */
  openRouterModel?: OpenRouterChatModelId;
  /** Gateway profile for this turn; used only when creating a new conversation. */
  gatewayProfile?: LlmGatewayProfile;
}

export interface ListChatConversationsFilters {
  contextEntityType?: ChatContextEntityType;
  contextEntityId?: string;
  includeArchived?: boolean;
}

export interface CreateChatConversationInput {
  title?: string;
  contextEntityType: ChatContextEntityType;
  contextEntityId?: string;
  gatewayProfile?: LlmGatewayProfile;
}

export interface UpdateChatConversationInput {
  title: string;
}

export interface UpdateChatConversationGatewayInput {
  gatewayProfile: LlmGatewayProfile;
}

export interface ChatConversationsResponse {
  conversations: ChatConversationItem[];
}

export interface ChatConversationMutationResponse {
  conversation: ChatConversationItem;
}

export type ChatAgentCapabilitySafety = 'read_only' | 'approval_required';

export interface ChatAgentCapabilityItem {
  name: string;
  description: string;
  permissions: string[];
  safety: ChatAgentCapabilitySafety;
  requiresApproval: boolean;
  destructive: boolean;
  available: boolean;
}

export interface ChatAgentCapabilitiesResponse {
  agentId: 'crm-chat-assistant';
  contextEntityType: ChatContextEntityType;
  contextEntityId?: string;
  capabilities: ChatAgentCapabilityItem[];
  summary: {
    total: number;
    available: number;
    readOnly: number;
    approvalRequired: number;
  };
}

export interface ChatTurnResponse {
  conversation: ChatConversationItem;
  messages: ChatMessageItem[];
  usage: Usage;
  traceId: string;
}

export interface ChatHistoryOptions {
  turnLimit?: number;
  olderCursor?: string;
}

export interface ChatConversationResponse {
  historyPage?: { turnLimit: number; hasMore: boolean; olderCursor: string | null };
  conversation: ChatConversationItem;
  messages: ChatMessageItem[];
  selectedEntity?: {
    type:
      | 'deal'
      | 'contact'
      | 'company'
      | 'task'
      | 'note'
      | 'document'
      | 'calendar_event'
      | 'attachment'
      | 'template'
      | 'contact_interaction';
    id: string;
    label: string;
  } | null;
  selectedDeal?: { id: string; label: string } | null;
}

export type ChatStreamEvent = AiChatEventEnvelope;

export interface ChatStreamCursor {
  eventId: string;
  runId: string;
  turnId: string;
  sequence: number;
}

export interface CancelChatTurnResponse {
  status: 'streaming' | 'resumed' | 'completed' | 'failed' | 'cancelled';
  cancelled: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Keep malformed runtime provider payloads from becoming `[object Object]`. */
export function normalizeChatErrorMessage(value: unknown, fallback: string): string {
  if (typeof value === 'string' && value.trim() !== '') return value;
  if (isRecord(value)) {
    for (const key of ['message', 'error', 'detail']) {
      const nested = value[key];
      if (typeof nested === 'string' && nested.trim() !== '') return nested;
    }
  }
  return fallback;
}

function normalizeChatStreamEvent(event: AiChatEventEnvelope): AiChatEventEnvelope {
  if (event.type === 'error') {
    const rawError = event.payload.error as unknown;
    const error = isRecord(rawError) ? rawError : {};
    return {
      ...event,
      payload: {
        state: 'failed',
        error: {
          message: normalizeChatErrorMessage(
            error.message,
            i18n.t('crm.ai_chat.error.request_failed'),
          ),
          ...(typeof error.code === 'string' ? { code: error.code } : {}),
          ...(event.payload.error.terminalOutcome === undefined
            ? {}
            : { terminalOutcome: event.payload.error.terminalOutcome }),
        },
      },
    };
  }

  if (
    event.type === 'turn_status' &&
    (event.payload.status === 'failed' || event.payload.status === 'cancelled') &&
    event.payload.error !== undefined
  ) {
    const rawError = event.payload.error as unknown;
    const error = isRecord(rawError) ? rawError : {};
    return {
      ...event,
      payload: {
        ...event.payload,
        error: {
          message: normalizeChatErrorMessage(
            error.message,
            i18n.t('crm.ai_chat.error.turn_failed'),
          ),
          ...(typeof error.code === 'string' ? { code: error.code } : {}),
          ...(typeof error.retryable === 'boolean' ? { retryable: error.retryable } : {}),
        },
      },
    };
  }

  return event;
}

function isCanonicalChatStreamEvent(value: unknown): value is AiChatEventEnvelope {
  return (
    isRecord(value) &&
    value.schemaVersion === 1 &&
    typeof value.eventId === 'string' &&
    typeof value.conversationId === 'string' &&
    typeof value.runId === 'string' &&
    typeof value.turnId === 'string' &&
    typeof value.sequence === 'number' &&
    isRecord(value.payload) &&
    (value.type === 'assistant_delta' ||
      value.type === 'reasoning_delta' ||
      value.type === 'tool_call' ||
      value.type === 'tool_result' ||
      value.type === 'approval_required' ||
      value.type === 'usage' ||
      value.type === 'structured_payload' ||
      value.type === 'client_action' ||
      value.type === 'turn_status' ||
      value.type === 'done' ||
      value.type === 'error')
  );
}

function buildConversationListPath(filters: ListChatConversationsFilters = {}): string {
  const params = new URLSearchParams();

  if (filters.contextEntityType !== undefined) {
    params.set('contextEntityType', filters.contextEntityType);
  }
  if (filters.contextEntityId !== undefined) {
    params.set('contextEntityId', filters.contextEntityId);
  }
  if (filters.includeArchived !== undefined) {
    params.set('includeArchived', String(filters.includeArchived));
  }

  const query = params.toString();
  return query ? `/chat/conversations?${query}` : '/chat/conversations';
}

function buildAgentCapabilitiesPath(filters: {
  contextEntityType: ChatContextEntityType;
  contextEntityId?: string;
}): string {
  const params = new URLSearchParams();
  params.set('contextEntityType', filters.contextEntityType);

  if (filters.contextEntityId !== undefined) {
    params.set('contextEntityId', filters.contextEntityId);
  }

  return `/chat/capabilities?${params.toString()}`;
}

async function parseStreamError(res: Response): Promise<ApiError> {
  let payload: {
    message?: string;
    error?: string;
    code?: string;
    details?: unknown;
    traceId?: string;
  } = {};

  try {
    payload = (await res.json()) as {
      message?: string;
      error?: string;
      code?: string;
      details?: unknown;
    };
  } catch {
    // Ignore parse failures and fall back to HTTP status text.
  }

  const message = normalizeChatErrorMessage(payload.message ?? payload.error, `HTTP ${res.status}`);
  const details =
    payload.traceId === undefined
      ? payload.details
      : {
          ...(isRecord(payload.details) ? payload.details : {}),
          traceId: payload.traceId,
        };
  return new ApiError(res.status, message, payload.code, details);
}

export async function getChatConversation(
  conversationId: string,
  options?: ChatHistoryOptions,
  signal?: AbortSignal,
): Promise<ChatConversationResponse> {
  const query = new URLSearchParams();
  if (options?.turnLimit !== undefined) query.set('turnLimit', String(options.turnLimit));
  if (options?.olderCursor !== undefined) query.set('olderCursor', options.olderCursor);
  const suffix = query.size === 0 ? '' : `?${query.toString()}`;
  return apiGet<ChatConversationResponse>(
    `/chat/${encodeURIComponent(conversationId)}${suffix}`,
    signal,
  );
}

export async function approveRemainingActionPlan(input: {
  conversationId: string;
  planId: string;
  token: string;
}): Promise<Extract<StructuredMessagePayload, { type: 'crm_action_plan_v1' }>> {
  return apiPost<
    Extract<StructuredMessagePayload, { type: 'crm_action_plan_v1' }>,
    { token: string; locale: 'ru' | 'en' }
  >(
    `/chat/${encodeURIComponent(input.conversationId)}/action-plans/${encodeURIComponent(input.planId)}/approve-remaining`,
    { token: input.token, locale: i18n.locale === 'en' ? 'en' : 'ru' },
  );
}

export async function requestActionPlanItemRetry(input: {
  conversationId: string;
  planId: string;
  itemId: string;
  failedExecutionId: string;
}): Promise<Extract<StructuredMessagePayload, { type: 'crm_action_plan_v1' }>> {
  return apiPost<
    Extract<StructuredMessagePayload, { type: 'crm_action_plan_v1' }>,
    { failedExecutionId: string }
  >(
    `/chat/${encodeURIComponent(input.conversationId)}/action-plans/${encodeURIComponent(input.planId)}/items/${encodeURIComponent(input.itemId)}/retry`,
    { failedExecutionId: input.failedExecutionId },
  );
}

export async function requestActionPlanItemUndo(input: {
  conversationId: string;
  planId: string;
  itemId: string;
}): Promise<Extract<StructuredMessagePayload, { type: 'crm_action_plan_v1' }>> {
  return apiPost<
    Extract<StructuredMessagePayload, { type: 'crm_action_plan_v1' }>,
    Record<string, never>
  >(`/chat/${input.conversationId}/action-plans/${input.planId}/items/${input.itemId}/undo`, {});
}

export async function listChatConversations(
  filters: ListChatConversationsFilters = {},
): Promise<ChatConversationsResponse> {
  return apiGet<ChatConversationsResponse>(buildConversationListPath(filters));
}

export async function getChatAgentCapabilities(filters: {
  contextEntityType: ChatContextEntityType;
  contextEntityId?: string;
}): Promise<ChatAgentCapabilitiesResponse> {
  return apiGet<ChatAgentCapabilitiesResponse>(buildAgentCapabilitiesPath(filters));
}

export async function createChatConversation(
  input: CreateChatConversationInput,
): Promise<ChatConversationMutationResponse> {
  return apiPost<ChatConversationMutationResponse, CreateChatConversationInput>(
    '/chat/conversations',
    input,
  );
}

export async function deleteChatConversations(
  conversationIds: string[],
): Promise<{ deleted: number }> {
  return apiDelete<{ deleted: number }, { conversationIds: string[] }>('/chat/conversations', {
    conversationIds,
  });
}

export async function updateChatConversation(
  conversationId: string,
  input: UpdateChatConversationInput,
): Promise<ChatConversationMutationResponse> {
  return apiPatch<ChatConversationMutationResponse, UpdateChatConversationInput>(
    `/chat/conversations/${conversationId}`,
    input,
  );
}

export async function updateChatConversationGateway(
  conversationId: string,
  input: UpdateChatConversationGatewayInput,
): Promise<ChatConversationMutationResponse> {
  return apiPatch<ChatConversationMutationResponse, UpdateChatConversationGatewayInput>(
    `/chat/conversations/${conversationId}/gateway`,
    input,
  );
}

export async function updateChatConversationAutoApprove(
  conversationId: string,
  enabled: boolean,
): Promise<ChatConversationMutationResponse> {
  return apiPatch<ChatConversationMutationResponse, { enabled: boolean }>(
    `/chat/conversations/${conversationId}/auto-approve`,
    { enabled },
  );
}

export async function shareChatConversation(
  conversationId: string,
): Promise<ChatConversationMutationResponse> {
  return apiPost<ChatConversationMutationResponse, Record<string, never>>(
    `/chat/conversations/${conversationId}/share`,
    {},
  );
}

export async function archiveChatConversation(
  conversationId: string,
): Promise<ChatConversationMutationResponse> {
  return apiPost<ChatConversationMutationResponse, Record<string, never>>(
    `/chat/conversations/${conversationId}/archive`,
    {},
  );
}

export async function cancelChatTurn(input: {
  conversationId: string;
  runId: string;
  turnId: string;
}): Promise<CancelChatTurnResponse> {
  return apiPost<CancelChatTurnResponse, { runId: string }>(
    `/chat/${input.conversationId}/turns/${input.turnId}/cancel`,
    { runId: input.runId },
  );
}

export async function* postChatMessageStream(
  conversationId: string,
  input: PostChatMessageInput,
  options: { signal?: AbortSignal; cursor?: ChatStreamCursor } = {},
): AsyncGenerator<ChatStreamEvent, void, void> {
  const headers = buildHeaders({
    Accept: 'text/event-stream',
    'Content-Type': 'application/json',
  });

  if (options.cursor !== undefined) {
    headers['Last-Event-ID'] = options.cursor.eventId;
    headers['X-AI-Chat-Run-Id'] = options.cursor.runId;
    headers['X-AI-Chat-Turn-Id'] = options.cursor.turnId;
    headers['X-AI-Chat-Sequence'] = String(options.cursor.sequence);
  }

  const res = await fetch(`/api/v1/chat/${conversationId}/message`, {
    method: 'POST',
    credentials: 'include',
    ...(options.signal ? { signal: options.signal } : {}),
    headers,
    body: JSON.stringify(input),
  });

  if (!res.ok) {
    throw await parseStreamError(res);
  }

  if (!res.body) {
    throw new ApiError(500, 'Streaming response has no body', 'empty_stream_body');
  }

  for await (const event of parseSseStream(res.body)) {
    if (event.data === '[DONE]') {
      return;
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(event.data);
    } catch {
      throw new ApiError(500, 'Streaming response contains malformed JSON', 'bad_stream_event');
    }
    if (isCanonicalChatStreamEvent(parsed)) {
      yield normalizeChatStreamEvent(parsed);
      continue;
    }

    throw new ApiError(
      500,
      'Streaming response contains unsupported event payload',
      'bad_stream_event',
    );
  }
}
